import { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  Box,
  Cell,
  CustomModalLayout,
  FormField,
  Layout,
  Modal,
  Checkbox,
  Input,
} from 'wix-style-react';
import { classes } from './NotificationSettingsModal.st.css';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { updateAccount } from 'src/redesign/features/Account/store/actions';
import { showNotification } from '../../Toast/store/actions';

type NotificationSettings = {
  notification_email?: string;
  new_order_confirmation: boolean;
  new_event_request_submitted: boolean;
  product_updates: boolean;
  transfer_requests: boolean;
  cancel_requests: boolean;
  refund_requests: boolean;
};

type NotificationSettingsModalProps = {
  isVisible?: boolean;
  setVisible: (value: boolean) => void;
};

export const NotificationSettingsModal: FC<NotificationSettingsModalProps> = ({
  isVisible,
  setVisible,
}) => {
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(accountSelector);
  const dispatch = useAppDispatch();

  const { handleSubmit, control } = useForm<NotificationSettings>({
    defaultValues: {
      notification_email: account?.notification_email ?? account?.email ?? '',
      new_order_confirmation: account?.notification_settings?.new_order_confirmation ?? true,
      new_event_request_submitted:
        account?.notification_settings?.new_event_request_submitted ?? true,
      product_updates: account?.notification_settings?.product_updates ?? true,
      transfer_requests: account?.notification_settings?.transfer_requests ?? true,
      cancel_requests: account?.notification_settings?.cancel_requests ?? true,
      refund_requests: account?.notification_settings?.refund_requests ?? true,
    },
  });

  const closeModal = () => {
    setVisible(false);
  };

  const onSubmit: SubmitHandler<NotificationSettings> = async (data) => {
    setLoading(true);

    try {
      // Extract notification_email from the data
      const { notification_email, ...notificationSettings } = data;

      await dispatch(
        updateAccount({
          notification_email,
          notification_settings: notificationSettings,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Notification Settings',
          detail: 'Settings updated successfully',
          life: 3000,
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update notification settings',
          life: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <Layout>
      <Cell span={12}>
        <Box direction="vertical" gap={3}>
          <div className={classes.heading}>Email Notifications</div>
          <div className={classes.subtext}>
            Choose which notifications you'd like to receive via email.
          </div>

          {/* Email Address Field */}
          <Controller
            control={control}
            name="notification_email"
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <FormField
                label="Notification Email Address"
                required
                infoContent="Enter the email address where you'd like to receive notifications. If left empty, we'll use your account email."
              >
                <Input
                  {...field}
                  status={fieldState.error ? 'error' : undefined}
                  statusMessage={fieldState.error?.message}
                  placeholder={account?.email || 'Enter email address'}
                  type="email"
                />
              </FormField>
            )}
          />

          <Box direction="vertical" gap={2}>
            {/* Order & Sales Notifications */}
            <Controller
              control={control}
              name="new_order_confirmation"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    New Order Confirmation
                  </Checkbox>
                </FormField>
              )}
            />

            {/* Event Management Notifications */}
            <Controller
              control={control}
              name="new_event_request_submitted"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    New Event Request Submitted
                  </Checkbox>
                </FormField>
              )}
            />

            {/* Customer Service Requests */}
            <Controller
              control={control}
              name="transfer_requests"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Transfer Requests
                  </Checkbox>
                </FormField>
              )}
            />

            <Controller
              control={control}
              name="cancel_requests"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Cancel Requests
                  </Checkbox>
                </FormField>
              )}
            />

            <Controller
              control={control}
              name="refund_requests"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Refund Requests
                  </Checkbox>
                </FormField>
              )}
            />

            {/* Product Updates */}
            <Controller
              control={control}
              name="product_updates"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Product Updates
                  </Checkbox>
                </FormField>
              )}
            />
          </Box>
        </Box>
      </Cell>
    </Layout>
  );

  const renderModalContent = () => (
    <CustomModalLayout
      primaryButtonText="Save Settings"
      primaryButtonProps={{ disabled: loading }}
      secondaryButtonText="Cancel"
      onCloseButtonClick={closeModal}
      primaryButtonOnClick={handleSubmit(onSubmit)}
      secondaryButtonOnClick={closeModal}
      title="Notification Settings"
      width="500px"
      height="700px"
    >
      {renderForm()}
    </CustomModalLayout>
  );

  return (
    <>
      <Modal isOpen={isVisible} onRequestClose={closeModal} screen="desktop">
        {renderModalContent()}
      </Modal>
    </>
  );
};
